import {
  Alert,
  Badge,
  BrandMark,
  Button,
  Card,
  ColorSwatch,
  DescriptionList,
  Inline,
  SettingsList,
  SettingsSection,
  SkeletonText,
  Stack,
  Text,
  useOptionalBrand,
  useToast,
  VisuallyHidden,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import {
  useAdminSettings,
  useCreateExternalIdType,
  useDeleteExternalIdType,
  useSaveWhiteLabel,
  type AdminSettings,
} from '../../api/queries/settings';
import { useExternalIdTypes } from '../../api/queries/users';
import { PageLayout } from '../../shell/Page';
import { PartnersSection } from './PartnersSection';
import { LineItemTableSection } from './LineItemTableSection';
import { PayHistorySection } from './PayHistorySection';
import { RolesSection } from './RolesSection';
import { changesFrom, draftFrom, hasChanges, WhiteLabelForm } from './WhiteLabelForm';
import { withReference } from '../../api/withReference';

/** The server's reason for a refused change, else a generic hint. */
export function reasonOf(error: unknown): string {
  if (error instanceof ApiError) {
    return error.problem.errors?.[0]?.message ?? error.problem.detail ?? error.problem.title;
  }
  return 'Check your connection and try again.';
}

const enabled = (on: boolean) =>
  on ? (
    <Badge tone="success" dot>
      Enabled
    </Badge>
  ) : (
    <Badge tone="neutral">Not enabled</Badge>
  );

/**
 * The white-label configuration in effect: each override, else the deployment's default,
 * else the brand pack's own (pc-o19.1, ADR 0018), laid out as the design system's
 * Settings screen (pc-dbb.16, pc-o19.8). Edit changes the overrides (pc-o19.3); saving
 * re-brands the app, Login included, at once.
 */
function WhiteLabel({ settings }: { settings: AdminSettings }) {
  const brand = useOptionalBrand();
  const save = useSaveWhiteLabel();
  const [draft, setDraft] = useState(() => draftFrom(settings));
  const changes = changesFrom(draft, settings);
  const { effective } = settings;
  const brandName = effective.brandName ?? brand?.brandName ?? '';
  const accent = effective.accent;
  const logo = effective.logo;

  const items: DescriptionListItem[] = [
    { term: 'Brand name', description: brandName },
    {
      term: 'Product name',
      description: effective.productName ?? brand?.productName ?? 'Payments Console',
    },
    {
      term: 'Accent colour',
      description: accent ? <ColorSwatch value={accent.original} /> : 'The brand’s own',
    },
    {
      term: 'Logo',
      description: logo ? (
        <Inline gap={3}>
          <Stack gap={1}>
            <Card>
              <BrandMark size="sm" name={brandName} logo={logo} />
            </Card>
            <Text size="meta" tone="muted">
              On the page
            </Text>
          </Stack>
          <Stack gap={1}>
            <Card data-surface="inverse">
              <BrandMark size="sm" name={brandName} logo={logo} />
            </Card>
            <Text size="meta" tone="muted">
              On ink surfaces
            </Text>
          </Stack>
        </Inline>
      ) : (
        'The brand’s own'
      ),
    },
    { term: 'Google SSO', description: enabled(settings.providers.google) },
    { term: 'Microsoft SSO', description: enabled(settings.providers.microsoft) },
    { term: 'Workspace', description: settings.workspaceHost },
    { term: 'Time zone', description: settings.workspaceTimeZone },
  ];

  // As the screen does: warn when the light mode can't use the accent as given.
  const adjusted = accent !== null && accent.used.light !== accent.original;

  return (
    <SettingsSection
      title="White-label configuration"
      description="The deployment sets the defaults and the sign-in providers; the brand name, product name, accent and logo set here override them."
      mode="form"
      editLabel="Edit branding"
      onEditingChange={(editing) => {
        if (editing) setDraft(draftFrom(settings));
      }}
      dirty={hasChanges(changes)}
      onSave={() =>
        save.mutateAsync(changes).catch((error: unknown) => {
          throw new Error(reasonOf(error));
        })
      }
      form={
        <WhiteLabelForm
          draft={draft}
          settings={settings}
          packName={brand?.brandName ?? ''}
          onChange={setDraft}
        />
      }
    >
      <Stack gap={4}>
        {adjusted && (
          <Alert tone="warning" title="Accent adjusted for contrast">
            {accent.original} is too hard to read against the page, so it shows as{' '}
            {accent.used.light} in light mode
            {accent.used.dark !== accent.original ? ` and ${accent.used.dark} in dark mode` : ''}.
          </Alert>
        )}
        <DescriptionList items={items} />
      </Stack>
    </SettingsSection>
  );
}

function ExternalIdTypes() {
  const types = useExternalIdTypes();
  const create = useCreateExternalIdType();
  const remove = useDeleteExternalIdType();
  const { toast } = useToast();
  const items = types.data ?? [];

  const onAdd = (name: string) =>
    create.mutateAsync(name).then(
      () => undefined,
      (error: unknown) => {
        toast(
          withReference(
            { tone: 'danger', title: `${name} couldn’t be added`, description: reasonOf(error) },
            error,
          ),
        );
        throw error;
      },
    );

  const onDelete = (id: string) => {
    const name = items.find((type) => type.id === id)?.name ?? 'The type';
    return remove.mutateAsync(id).then(
      () => undefined,
      (error: unknown) => {
        toast(
          withReference(
            {
              tone: 'danger',
              title: `${name} couldn’t be deleted`,
              description: reasonOf(error),
            },
            error,
          ),
        );
        throw error;
      },
    );
  };

  return (
    <SettingsSection
      title="External ID types"
      description="The kinds of ID a user can have, one of each. Changes here apply to this workspace only."
      mode="list"
    >
      <SettingsList
        items={items}
        itemLabel="external ID type"
        usageLabel={['user', 'users']}
        onAdd={onAdd}
        onDelete={onDelete}
        emptyLabel="No external ID types yet. Add one to record it for users."
      />
    </SettingsSection>
  );
}

/**
 * Settings (pc-o19.2): the white-label configuration, read-only, then External ID types
 * (add and delete; the API refuses deleting a type users have, and the toast says so).
 */
export function SettingsPage() {
  const settings = useAdminSettings();
  const types = useExternalIdTypes();

  let body;
  if (settings.isError || types.isError) {
    body = (
      <Alert
        tone="danger"
        title="Settings couldn’t be loaded"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (settings.isError) void settings.refetch();
              if (types.isError) void types.refetch();
            }}
          >
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (!settings.data || !types.data) {
    body = (
      <>
        <SkeletonText lines={8} />
        <VisuallyHidden role="status">Loading settings</VisuallyHidden>
      </>
    );
  } else {
    body = (
      <>
        <WhiteLabel settings={settings.data} />
        {settings.data.partners.length > 0 && <PartnersSection partners={settings.data.partners} />}
        <RolesSection settings={settings.data} />
        <LineItemTableSection />
        <PayHistorySection settings={settings.data} />
        <ExternalIdTypes />
      </>
    );
  }

  return (
    <PageLayout page="settings">
      <Stack gap={5} measure="prose">
        {body}
      </Stack>
    </PageLayout>
  );
}
